import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';
import {EdgeRemoveChange} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeFlowchartTaskSchemaEventMetadata,
  FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_ENDED_EVENT,
  FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_STARTED_EVENT,
} from 'performance/processProcessesEvents';
import {NodeDetailsSidepanelContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import {createTaskNode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import CreateProcessTaskDefinitionEditor from 'processes/settings/CreateProcessTaskDefinitionEditor';
import {buildExecutionNodeTypeIdentifier} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/useFlowchartStateAnalytics';
import {cacheCreatedTaskDefinition} from 'processes/settings/ProcessTaskSchemaUtils';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import {useFlowchartSidepanelManagerContext} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';
import {
  SidepanelChange,
  SidepanelType,
} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelManager';
import {useFlowchartSidepanelTabUnsavedChanges} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelTabUnsavedChanges';
import SearchInput from 'ui-library/SearchInput';
import APIUtil from 'util/APIUtil';

import {ProcessTaskSchemaGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {buildControlFlowEdge} from '../buildStaticDataFromFlowchartConfigGraphQL';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
} from '../processFlowchartConfigEditorConstants';
import {
  isPlaceholderForAddExistingTaskSchemaData,
  isPlaceholderForTaskSchemaCreateData,
  PlaceholderNode,
} from '../processFlowchartConfigEditorNodeTypes';
import {createStaticNodeDataFromTaskDefinitionVersion} from '../processFlowchartConfigEditorUtils';
import {ConfigurableTaskDefinitionGQL, isConfigurableTaskDefinition} from '../useAllSchemasById';

export function AddExistingTaskDefinitionPane(props: {
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  createNodeFromTaskDefinition: (
    taskDefinition: ConfigurableTaskDefinitionGQL,
    shouldOpenSidepanel: boolean
  ) => Promise<void>;
  flowchartTypeName: string;
}): JSX.Element {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'AddExistingTaskDefinitionPane', element: 'root'})}
    >
      <SearchInput
        placeholder="Search"
        doSearch={(newSearchQuery) => {
          setSearchQuery(newSearchQuery);
          if (newSearchQuery != null && newSearchQuery.length > 0) {
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.SEARCHED_TASK_SCHEMAS_IN_CANVAS, {
              actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
              searchSource: 'right_sidepanel',
              flowchartType: props.flowchartTypeName,
            });
          }
        }}
        query={searchQuery}
      />
      <div className={css(styles.addExistingTaskSchemaOptionsContainer)}>
        {props.taskDefinitionsForSidepanel
          .toSorted((left, right) =>
            left.latestVersion.processTaskSchema.name.localeCompare(
              right.latestVersion.processTaskSchema.name
            )
          )
          .filter(
            (taskDefinition) =>
              !isFlowchartType(taskDefinition.latestVersion.processTaskSchema.executionType) &&
              (searchQuery == null ||
                taskDefinition.latestVersion.processTaskSchema.name
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()))
          )
          .map((taskDefinition) => (
            <Button
              buttonStyle={ButtonStyle.UNBUTTON}
              key={taskDefinition.id}
              fullWidth={true}
              alignContentLeft={true}
              icon={ModelTypeConstants.PROCESS_TASK}
              textClassName={css(styles.addExistingTaskSchemaOptionText)}
              text={taskDefinition.latestVersion.processTaskSchema.name}
              className={css(styles.addExistingTaskSchemaOption)}
              onClick={() => {
                props.createNodeFromTaskDefinition(taskDefinition, false);
              }}
              dataTestAttrs={{
                component: 'AddExistingTaskDefinitionPane',
                element: 'TaskDefinitionOption',
                key: taskDefinition.id,
              }}
            />
          ))}
      </div>
    </div>
  );
}

export default function PlaceholderNodeDetailsSidepanelContent(props: {
  selectedNode: PlaceholderNode;
}): JSX.Element {
  const {
    flowchartTaskSchema,
    flowchartConfigId,
    applyFlowchartChanges,
    shouldAddEdgesForNewNode,
    interactiveFlowchartOptions,
    allSchemasById,
    interactiveFlowchart,
    taskDefinitionsForSidepanel,
    flowchartEditorConfigurator,
  } = NodeDetailsSidepanelContext.useContext();

  const sidepanelManager = useFlowchartSidepanelManagerContext();

  const onDiscard = useCallback(async () => {
    await applyFlowchartChanges((flowchartDraftState) => ({
      nodeChanges: [
        {
          type: 'remove',
          id: props.selectedNode.id,
        },
      ],
      edgeChanges: [],
    }));
  }, [applyFlowchartChanges, props.selectedNode.id]);

  const {setIsTabDirty} = useFlowchartSidepanelTabUnsavedChanges(
    `placeholder-${props.selectedNode.id}`,
    'Node Configuration',
    onDiscard
  );

  useOnMount(() => {
    setIsTabDirty(true);
  });

  const allTaskSchemas = useMemo(
    () =>
      Object.values(allSchemasById).filter(
        (schema): schema is ProcessTaskSchemaGraphQL =>
          schema != null && APIUtil.isProcessTaskSchema(schema.id)
      ),
    [allSchemasById]
  );

  const createNodeFromTaskDefinition = async (
    taskDefinition: ConfigurableTaskDefinitionGQL,
    shouldOpenSidepanel: boolean = true
  ): Promise<void> => {
    const newId = uuidv4();
    const taskSchema = assertIsAccessible(taskDefinition.latestVersion.processTaskSchema);
    const newAllTaskSchemas = [...allTaskSchemas];
    if (allTaskSchemas.find(({id}) => id === taskSchema.id) == null) {
      newAllTaskSchemas.push(taskSchema);
    }
    const staticNodeData = createStaticNodeDataFromTaskDefinitionVersion({
      nodeClientId: newId,
      taskDefinitionVersion: taskDefinition.latestVersion,
      nodeDetailsType: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
      allTaskSchemas: newAllTaskSchemas,
      editorMode: interactiveFlowchartOptions.editorMode,
    });
    const taskNode = createTaskNode(staticNodeData, props.selectedNode.position, interactiveFlowchartOptions);

    // We need to replace any edges that are connected to the placeholder node with edges to the new
    // node.
    const edgesToAdd: Array<FlowchartStaticEdgeData> = [];
    const edgeRemoveChanges: Array<EdgeRemoveChange> = [];
    for (const edge of interactiveFlowchart.edges) {
      if (edge.source !== props.selectedNode.id && edge.target !== props.selectedNode.id) {
        continue;
      }
      edgeRemoveChanges.push({
        type: 'remove',
        id: edge.id,
      });
      assert(
        edge.type !== ProcessFlowchartEdgeType.DATA_FLOW,
        'Data flow edges should not be connected to the placeholder node'
      );
      const newSource = edge.source === props.selectedNode.id ? newId : edge.source;
      const newTarget = edge.target === props.selectedNode.id ? newId : edge.target;
      const staticEdge = buildControlFlowEdge({
        sourceNodeId: newSource,
        targetNodeId: newTarget,
      });
      edgesToAdd.push(staticEdge);
    }

    if (!(await shouldAddEdgesForNewNode(edgesToAdd, taskNode.data))) {
      return;
    }

    setIsTabDirty(false);

    let sidepanelChange: SidepanelChange = {
      sidepanel: null,
    };

    if (shouldOpenSidepanel) {
      sidepanelChange = {
        sidepanel: SidepanelType.NODE_SETTINGS,
        nodeId: taskNode.id,
      };
    }
    await sidepanelManager.changeSidepanel({
      sidepanelChange,
      onBeforeSidepanelChanged: async () => {
        await applyFlowchartChanges((flowchartDraftState) => ({
          nodeChanges: [
            {
              type: 'remove',
              id: props.selectedNode.id,
            },
            {
              type: 'add',
              item: taskNode,
            },
          ],
          edgeChanges: [
            ...edgeRemoveChanges,
            ...edgesToAdd.map((edge) => ({type: 'add' as const, item: edge})),
          ],
        }));
      },
    });
  };

  if (isPlaceholderForTaskSchemaCreateData(props.selectedNode.data.placeholderForNodeTypeData)) {
    return (
      <div {...getTestProps({component: 'PlaceholderNodeDetailsSidepanelContent', element: 'root'})}>
        <CreateProcessTaskDefinitionEditor
          restrictedToExecutionType={props.selectedNode.data.placeholderForNodeTypeData.executionType}
          restrictedToOrganizationId={flowchartTaskSchema.organization.id}
          stickyCreateButton={true}
          onCreateProcessTaskDefinition={async (newTaskDefinition) => {
            logProfilerTimingEvent(FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_STARTED_EVENT);
            assert(
              isConfigurableTaskDefinition(newTaskDefinition),
              'Creating a new task definition is expected to return a ConfigurableTaskDefinitionGQL'
            );
            cacheCreatedTaskDefinition(newTaskDefinition);
            await createNodeFromTaskDefinition(newTaskDefinition, false);
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CREATED_NEW_TASK_DEFINITION_FROM_FLOWCHART, {
              actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
              nodeType: buildExecutionNodeTypeIdentifier(
                ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
                newTaskDefinition.latestVersion.processTaskSchema.executionType
              ),
              taskDefinitionVersionId: newTaskDefinition.latestVersion.id,
              taskSchemaId: newTaskDefinition.latestVersion.processTaskSchema.id,
              executionType: newTaskDefinition.latestVersion.processTaskSchema.executionType,
              flowchartType: flowchartEditorConfigurator.flowchartTypeName,
            });
            logProfilerTimingEvent(
              FLOWCHART_CONFIG_TASK_SCHEMA_CREATE_ENDED_EVENT,
              composeFlowchartTaskSchemaEventMetadata({
                flowchartType: flowchartEditorConfigurator.flowchartTypeName,
                executionType: newTaskDefinition.latestVersion.processTaskSchema.executionType,
                flowchartConfigId,
              })
            );
          }}
        />
      </div>
    );
  }
  if (isPlaceholderForAddExistingTaskSchemaData(props.selectedNode.data.placeholderForNodeTypeData)) {
    const {allowedTargetTaskSchemaIds} = props.selectedNode.data.placeholderForNodeTypeData;
    const flowchartOrganization = flowchartTaskSchema.organization;
    let allowedTaskDefinitions = taskDefinitionsForSidepanel.filter(
      (definition) =>
        !isArchived(definition.latestVersion.processTaskSchema) &&
        flowchartOrganization.id === definition.latestVersion.processTaskSchema.organization.id
    );
    // In ACM/PCM mode with >10 targets, the placeholder carries the set of valid transition
    // target task schema IDs so we only show reachable schemas rather than the full org list.
    if (allowedTargetTaskSchemaIds != null) {
      allowedTaskDefinitions = allowedTaskDefinitions.filter((definition) =>
        allowedTargetTaskSchemaIds.has(definition.latestVersion.processTaskSchema.id)
      );
    }
    return (
      <div {...getTestProps({component: 'PlaceholderNodeDetailsSidepanelContent', element: 'root'})}>
        <AddExistingTaskDefinitionPane
          createNodeFromTaskDefinition={createNodeFromTaskDefinition}
          taskDefinitionsForSidepanel={allowedTaskDefinitions}
          flowchartTypeName={flowchartEditorConfigurator.flowchartTypeName}
        />
      </div>
    );
  } else {
    assert(false, 'Unexpected placeholder type', {
      placeholderForNodeTypeData: props.selectedNode.data.placeholderForNodeTypeData,
    });
    return <></>;
  }
}

const styles = StyleSheet.create({
  wrapper: {
    marginLeft: 8,
  },
  addExistingTaskSchemaOption: {
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 8,
    marginBottom: 8,
    border: `1px solid ${Color.GRAY_200}`,
    ':hover': {
      border: `1px solid ${Color.BLUE_500}`,
      backgroundColor: Color.BLUE_50,
    },
    ':focus': {
      border: `1px solid ${Color.BLUE_500}`,
      backgroundColor: Color.BLUE_50,
      boxShadow: 'none',
    },
  },
  addExistingTaskSchemaOptionText: {
    marginLeft: 8,
  },
  addExistingTaskSchemaOptionsContainer: {
    marginTop: 8,
  },
});
